import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import SiteHeader from '../components/SiteHeader';
import SiteFooter from '../components/SiteFooter';
import CountdownTimer from '../components/CountdownTimer';
import heroMockupImg from '../assets/webp-mockups/hero-mockup-cropped.webp';
import cityMapMockup from '../assets/webp-mockups/city-map-upright.webp';
import nearHotelMockup from '../assets/webp-mockups/near-my-hotel-upright.webp';
import preferencesMockup from '../assets/webp-mockups/preferences-upright.webp';
import nearbyMockup from '../assets/webp-mockups/nearby-upright.webp';
import check3dImg from '../assets/check-3d.png';
import evanImg from '../assets/evan.png';
import maleProfile2Img from '../assets/male-profile-2.png';
import sophieImg from '../assets/sophie.png';
import ambaUkImg from '../assets/amba-uk.png';
import friendsCampingImg from '../assets/evan.png';

const lexend = '"Lexend", sans-serif';

const featureSections = [
  {
    header: "Don't be stuck somewhere that isn't for you.",
    subheader: "Instead of walking miles to the nearest gym or supermarket, find the part of town with the facilities you want and need, all within reach.",
    image: null,
    imageAlt: 'Traveler',
    mockup: preferencesMockup,
    mockupAlt: 'NearStay preferences',
    isImageLeft: true,
  },
  {
    header: "What Would Mom Think?",
    subheader: "Don't spend your trip looking over your shoulder. Stay in the safest areas. NearStay points you to the safest neighbourhoods, mapped out for you.",
    image: null,
    imageAlt: 'Safe city',
    mockup: cityMapMockup,
    mockupAlt: 'NearStay city map',
    isImageLeft: false,
    mockupOffsetMore: true,
  },
  {
    header: "Don't miss the best nightlife spots.",
    subheader: "Whether its rooftop bars, nightclubs or areas with other travelers, NearStay knows where all the fun is at.",
    image: null,
    imageAlt: 'Nightlife',
    mockup: nearbyMockup,
    mockupAlt: 'NearStay nearby',
    isImageLeft: true,
  },
  {
    header: "Worried about overpaying as a tourist?",
    subheader: "NearStay secures the areas with the best value for money so you don't have to. Pay less for more.",
    image: null,
    imageAlt: 'Value',
    mockup: nearHotelMockup,
    mockupAlt: 'NearStay value areas',
    isImageLeft: false,
    mockupOffsetMore: true,
  },
];

const reviews = [
  { quote: "My parents felt so much better knowing I found the right area for my trip to Paris and I was safe.", name: "Amba", location: "London, UK", avatar: ambaUkImg },
  { quote: "Found the perfect spot for partying and going on dates. The app knew exactly what I was looking for.", name: "Evan", location: "Austin, TX", avatar: evanImg },
  { quote: "Best value for money I've ever had on a trip. I'd recommend this for anyone looking to avoid the overpriced tourist traps.", name: "Sophie", location: "Seattle, Washington", avatar: sophieImg },
  { quote: "Got an area with the best gym and restaurants just for me. This app understood what I needed.", name: "David", location: "Denver, Colorado", avatar: maleProfile2Img },
];

function Stars() {
  return (
    <span style={{
      color: '#fcca03',
      fontSize: '1rem',
      letterSpacing: '0.35em',
      textShadow: '0 1px 2px rgba(0,0,0,0.1)',
    }} aria-hidden="true">
      ★★★★★
    </span>
  );
}

function HomePage(props = {}) {
  const {
    heroTitle = "Don't miss out on the best parts of town.",
    heroSubtitle = "NearStay helps you find the safest, most energetic and best value parts of the city for your stay. Find the area that's right for you.",
    showCountdown = true,
    featureSectionsOverride,
    headerCtaLabel = 'Join The Waitlist',
    showHeaderTimer = true,
    heroCtaLabel = 'Join The Waitlist',
    sectionCtaLabel = 'Join The Waitlist',
    reviewsHeading = "Here's What Other Adventurous Early Users Have Said",
    reviewsOverride,
  } = props;
  const sections = featureSectionsOverride || featureSections;
  const reviewCards = reviewsOverride || reviews;
  const [showWaitlistModal, setShowWaitlistModal] = useState(false);
  const [modalStep, setModalStep] = useState('form');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [friendEmails, setFriendEmails] = useState(['']);

  const openWaitlistModal = () => {
    setModalStep('form');
    setFriendEmails(['']);
    setShowWaitlistModal(true);
  };
  const closeWaitlistModal = () => {
    setShowWaitlistModal(false);
    setModalStep('form');
    setFriendEmails(['']);
  };

  const handleWaitlistSubmit = async (e) => {
    e.preventDefault();
    if (!agreed) return;
    const form = e.target;
    const formData = new FormData(form);
    try {
      const res = await fetch('https://formspree.io/f/xpqjpzge', {
        method: 'POST',
        body: formData,
        headers: { Accept: 'application/json' },
      });
      if (res.ok) setModalStep('invite');
    } catch {
      setModalStep('invite');
    }
  };

  const addFriendEmail = () => setFriendEmails((prev) => [...prev, '']);
  const updateFriendEmail = (i, v) => setFriendEmails((prev) => {
    const next = [...prev];
    next[i] = v;
    return next;
  });

  const handleInviteSubmit = async (e) => {
    e.preventDefault();
    const validEmails = friendEmails.filter(Boolean);
    if (validEmails.length > 0) {
      const formData = new FormData();
      validEmails.forEach((addr) => formData.append('friend_email', addr));
      try {
        await fetch('https://formspree.io/f/xnjbpqej', {
          method: 'POST',
          body: formData,
          headers: { Accept: 'application/json' },
        });
      } catch { /* continue to success regardless */ }
    }
    setModalStep('success');
  };

  const handleNoThanks = () => setModalStep('success');

  return (
    <div className="App" style={{ background: '#fff' }}>
      <SiteHeader onWaitlistClick={openWaitlistModal} ctaLabel={headerCtaLabel} showTimer={showHeaderTimer} />

      {/* Hero Section */}
      <section className="site-hero" style={{
        position: 'relative',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '4rem 2rem 2.5rem',
      }}>
        <div style={{
          maxWidth: '1200px',
          width: '100%',
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '4rem',
          alignItems: 'center',
        }}>
          {/* Left: Copy + Waitlist */}
          <div className="site-hero-copy" style={{ textAlign: 'left' }}>
            <h1 style={{
              fontFamily: lexend,
              fontOpticalSizing: 'auto',
              fontWeight: 800,
              fontSize: 'clamp(2.25rem, 4.5vw, 3.25rem)',
              color: '#1c3dfe',
              lineHeight: 1.25,
              marginBottom: '1rem',
            }}>
              {heroTitle}
            </h1>
            <p style={{
              fontFamily: lexend,
              fontOpticalSizing: 'auto',
              fontSize: '1.35rem',
              color: '#444',
              lineHeight: 1.5,
              marginBottom: '2.25rem',
              maxWidth: '32em',
            }}>
              {heroSubtitle}
            </p>
            {showCountdown && (
              <div className="site-hero-countdown" style={{ marginBottom: '2rem', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '0.75rem' }}>
                <span className="site-hero-50-places" style={{
                  fontFamily: lexend,
                  fontOpticalSizing: 'auto',
                  fontSize: '1.05rem',
                  fontWeight: 500,
                  color: '#1c3dfe',
                  animation: 'pulseOnLoad 0.6s ease-in-out 5',
                }}>
                  Only <strong style={{ fontWeight: 700 }}>50 Places</strong> Left!
                </span>
                <CountdownTimer label="Closes In" />
              </div>
            )}

            <button
              type="button"
              className="site-waitlist-btn"
              onClick={openWaitlistModal}
              style={{
                padding: '0.85rem 2rem',
                background: '#c038ff',
                color: 'white',
                border: 'none',
                borderRadius: '9999px',
                fontFamily: lexend,
                fontOpticalSizing: 'auto',
                fontSize: '1rem',
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'opacity 0.2s ease',
              }}
              onMouseOver={(e) => { e.currentTarget.style.opacity = '0.9'; }}
              onMouseOut={(e) => { e.currentTarget.style.opacity = '1'; }}
            >
              {heroCtaLabel}
            </button>
          </div>

          {/* Right: Hero Image */}
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}>
            <img
              className="site-hero-mockup"
              src={heroMockupImg}
              alt="NearStay app"
              style={{
                maxWidth: '100%',
                height: 'auto',
                maxHeight: '70vh',
                objectFit: 'contain',
              }}
            />
          </div>
        </div>
        <div
          style={{
            position: 'absolute',
            bottom: '2rem',
            left: '50%',
            display: 'flex',
            justifyContent: 'center',
            animation: 'chevronBounce 2s ease-in-out infinite',
          }}
          aria-hidden="true"
        >
          <svg
            width="32"
            height="32"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ color: '#1c3dfe' }}
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </div>
      </section>

      {/* Feature Sections */}
      {sections.map((section, i) => {
        const visualBlock = (
          <div
            key="visual"
            className="site-feature-visual"
            style={{
              position: 'relative',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '660px',
              marginRight: section.isImageLeft ? '4rem' : 0,
              marginLeft: section.isImageLeft ? 0 : '4rem',
            }}
          >
            {/* Optional background visual */}
            {section.image ? (
              <img
                src={section.image}
                alt={section.imageAlt}
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(calc(-50% - 120px), -50%)',
                  maxWidth: '85%',
                  height: 'auto',
                  maxHeight: '640px',
                  objectFit: 'contain',
                  borderRadius: '12px',
                  zIndex: 0,
                }}
              />
            ) : null}
            {/* Mockup in front - offset right to reveal more image */}
            <img
              src={section.mockup}
              alt={section.mockupAlt}
              style={{
                position: 'relative',
                zIndex: 1,
                maxWidth: '85%',
                height: 'auto',
                maxHeight: '500px',
                objectFit: 'contain',
                marginLeft: section.image ? (section.mockupOffsetMore ? '200px' : '140px') : '0px',
              }}
            />
          </div>
        );
        const textBlock = (
          <div key="text" className="site-feature-text" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <h2 style={{
              fontFamily: lexend,
              fontOpticalSizing: 'auto',
              fontWeight: 800,
              fontSize: 'clamp(1.75rem, 3.5vw, 2.5rem)',
              color: '#1c3dfe',
              lineHeight: 1.25,
              marginBottom: '1rem',
            }}>
              {section.header}
            </h2>
            <p style={{
              fontFamily: lexend,
              fontOpticalSizing: 'auto',
              fontSize: '1.15rem',
              color: '#444',
              lineHeight: 1.6,
              marginBottom: '1.5rem',
            }}>
              {section.subheader}
            </p>
            <button
              type="button"
              className="site-waitlist-btn"
              onClick={openWaitlistModal}
              style={{
                alignSelf: 'flex-start',
                padding: '0.75rem 1.75rem',
                background: '#c038ff',
                color: 'white',
                border: 'none',
                borderRadius: '9999px',
                fontFamily: lexend,
                fontOpticalSizing: 'auto',
                fontSize: '1rem',
                fontWeight: 600,
                cursor: 'pointer',
                transition: 'opacity 0.2s ease',
              }}
              onMouseOver={(e) => { e.currentTarget.style.opacity = '0.9'; }}
              onMouseOut={(e) => { e.currentTarget.style.opacity = '1'; }}
            >
              {sectionCtaLabel}
            </button>
          </div>
        );
        return (
          <section
            key={i}
            className="site-section"
            style={{
              padding: '2.5rem 2rem',
              background: i % 2 === 0 ? '#fff' : '#f8f9fa',
            }}
          >
            <div
              className="site-feature-section-grid"
              style={{
                maxWidth: '1200px',
                margin: '0 auto',
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '3rem',
                alignItems: 'center',
              }}
            >
              {section.isImageLeft ? (
                <>
                  {visualBlock}
                  {textBlock}
                </>
              ) : (
                <>
                  {textBlock}
                  {visualBlock}
                </>
              )}
            </div>
          </section>
        );
      })}

      {/* Social Proof / Reviews Section */}
      <section className="reviews-section" style={{
        padding: '2.5rem 2rem',
        background: '#fff',
        borderTop: '1px solid #eee',
      }}>
        <div className="reviews-section-inner" style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <h2 style={{
            fontFamily: lexend,
            fontOpticalSizing: 'auto',
            fontWeight: 800,
            fontSize: 'clamp(1.5rem, 3vw, 2rem)',
            color: '#1c3dfe',
            textAlign: 'center',
            marginBottom: '2rem',
          }}>
            {reviewsHeading}
          </h2>
          <div className="reviews-cards-and-party">
            <div className="reviews-cards-row">
            {reviewCards.map((r, i) => (
              <div
                key={i}
                className="review-card"
                style={{
                  padding: '1.5rem',
                  background: '#f8f9fa',
                  borderRadius: '12px',
                  border: '1px solid #eee',
                }}
              >
                {r.avatar && (
                  <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1rem' }}>
                    <img
                      src={r.avatar}
                      alt=""
                      style={{
                        width: '64px',
                        height: '64px',
                        borderRadius: '50%',
                        objectFit: 'cover',
                        maxWidth: '100%',
                      }}
                    />
                  </div>
                )}
                <p style={{
                  fontFamily: lexend,
                  fontOpticalSizing: 'auto',
                  fontSize: '0.95rem',
                  color: '#333',
                  lineHeight: 1.5,
                  marginBottom: '1rem',
                  fontStyle: 'italic',
                }}>
                  "{r.quote}"
                </p>
                <div style={{ marginBottom: '0.5rem' }}>
                  <Stars />
                </div>
                <p style={{
                  fontFamily: lexend,
                  fontOpticalSizing: 'auto',
                  fontSize: '0.85rem',
                  color: '#666',
                  margin: 0,
                }}>
                  — {r.name}, {r.location}
                </p>
              </div>
            ))}
            </div>
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: '1.25rem' }}>
              <button
                type="button"
                className="site-waitlist-btn"
                onClick={openWaitlistModal}
                style={{
                  padding: '0.6rem 1.25rem',
                  background: '#c038ff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '9999px',
                  fontFamily: lexend,
                  fontOpticalSizing: 'auto',
                  fontSize: '0.95rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'opacity 0.2s ease',
                }}
                onMouseOver={(e) => { e.currentTarget.style.opacity = '0.9'; }}
                onMouseOut={(e) => { e.currentTarget.style.opacity = '1'; }}
              >
                Skyrocket My Bookings
              </button>
            </div>
          </div>
        </div>
      </section>

      <SiteFooter />

      {/* Waitlist modal */}
      {showWaitlistModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000,
          }}
          onClick={closeWaitlistModal}
        >
          <div
            style={{
              position: 'relative',
              background: 'white',
              padding: '2rem',
              paddingTop: '2.5rem',
              borderRadius: '16px',
              maxWidth: '440px',
              width: '90%',
              boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={closeWaitlistModal}
              aria-label="Close"
              style={{
                position: 'absolute',
                top: '1rem',
                right: '1rem',
                width: '36px',
                height: '36px',
                padding: 0,
                background: 'transparent',
                border: 'none',
                borderRadius: '50%',
                fontSize: '1.5rem',
                lineHeight: 1,
                color: '#666',
                cursor: 'pointer',
              }}
            >
              ×
            </button>
            {modalStep === 'form' ? (
              <>
            <h2 style={{
              fontFamily: lexend,
              fontOpticalSizing: 'auto',
              fontWeight: 700,
              fontSize: '1.5rem',
              color: '#000',
              marginBottom: '1.5rem',
            }}>
              Join the waitlist
            </h2>
            <form
              action="https://formspree.io/f/xpqjpzge"
              method="POST"
              onSubmit={handleWaitlistSubmit}
              style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}
            >
              <input
                type="text"
                name="name"
                placeholder="Enter your name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                style={{
                  width: '100%',
                  padding: '0.85rem 1.25rem',
                  borderRadius: '8px',
                  border: '2px solid #e0e0e0',
                  fontFamily: lexend,
                  fontOpticalSizing: 'auto',
                  fontSize: '1rem',
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
                onFocus={(e) => { e.target.style.borderColor = '#1c3dfe'; }}
                onBlur={(e) => { e.target.style.borderColor = '#e0e0e0'; }}
              />
              <input
                type="email"
                name="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                style={{
                  width: '100%',
                  padding: '0.85rem 1.25rem',
                  borderRadius: '8px',
                  border: '2px solid #e0e0e0',
                  fontFamily: lexend,
                  fontOpticalSizing: 'auto',
                  fontSize: '1rem',
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
                onFocus={(e) => { e.target.style.borderColor = '#1c3dfe'; }}
                onBlur={(e) => { e.target.style.borderColor = '#e0e0e0'; }}
              />
              <label style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '0.75rem',
                fontFamily: lexend,
                fontOpticalSizing: 'auto',
                fontSize: '0.9rem',
                color: '#444',
                lineHeight: 1.5,
                cursor: 'pointer',
              }}>
                <input
                  type="checkbox"
                  checked={agreed}
                  onChange={(e) => setAgreed(e.target.checked)}
                  required
                  style={{ marginTop: '0.25rem', flexShrink: 0 }}
                />
                <span>
                  I am an adventurous traveler, eager to use NearStay for finding my perfect area to stay in. I agree to the{' '}
                  <Link to="/privacy" style={{ color: '#1c3dfe', textDecoration: 'underline' }}>
                    NearStay Privacy Policy
                  </Link>.
                </span>
              </label>
              <div style={{ marginTop: '0.5rem' }}>
                <button
                  type="submit"
                  disabled={!agreed}
                  style={{
                    width: '100%',
                    padding: '0.85rem 1.5rem',
                    background: agreed ? '#1c3dfe' : '#ccc',
                    color: 'white',
                    border: 'none',
                    borderRadius: '9999px',
                    fontFamily: lexend,
                    fontOpticalSizing: 'auto',
                    fontSize: '1rem',
                    fontWeight: 600,
                    cursor: agreed ? 'pointer' : 'not-allowed',
                    boxShadow: agreed ? '0 4px 14px rgba(28, 61, 254, 0.4)' : 'none',
                  }}
                >
                  Sign Up
                </button>
              </div>
            </form>
              </>
            ) : modalStep === 'invite' ? (
              <>
            <img
              src={friendsCampingImg}
              alt=""
              style={{
                width: '100%',
                maxHeight: '140px',
                objectFit: 'cover',
                objectPosition: 'top',
                borderRadius: '12px',
                marginBottom: '1.25rem',
              }}
            />
            <div style={{
              marginBottom: '1.5rem',
              textAlign: 'center',
            }}>
              <p style={{
                fontFamily: lexend,
                fontOpticalSizing: 'auto',
                fontWeight: 700,
                fontSize: '1.1rem',
                color: '#000',
                margin: '0 0 0.25rem 0',
              }}>
                Feel like your friends will like NearStay?
              </p>
              <p style={{
                fontFamily: lexend,
                fontOpticalSizing: 'auto',
                fontWeight: 700,
                fontSize: '1.1rem',
                color: '#000',
                margin: 0,
              }}>
                Send Them An Invite
              </p>
            </div>
            <form
              action="https://formspree.io/f/xnjbpqej"
              method="POST"
              onSubmit={handleInviteSubmit}
              style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}
            >
              {friendEmails.map((addr, i) => (
                <div key={i} style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  <input
                    type="email"
                    name="friend_email"
                    placeholder="Friend's email"
                    value={addr}
                    onChange={(e) => updateFriendEmail(i, e.target.value)}
                    style={{
                      flex: 1,
                      padding: '0.75rem 1rem',
                      borderRadius: '8px',
                      border: '2px solid #e0e0e0',
                      fontFamily: lexend,
                      fontOpticalSizing: 'auto',
                      fontSize: '0.95rem',
                      outline: 'none',
                      boxSizing: 'border-box',
                    }}
                    onFocus={(e) => { e.target.style.borderColor = '#1c3dfe'; }}
                    onBlur={(e) => { e.target.style.borderColor = '#e0e0e0'; }}
                  />
                  {i === friendEmails.length - 1 ? (
                    <button
                      type="button"
                      onClick={addFriendEmail}
                      aria-label="Add another"
                      style={{
                        width: '44px',
                        height: '44px',
                        flexShrink: 0,
                        borderRadius: '50%',
                        border: '2px solid #1c3dfe',
                        background: '#1c3dfe',
                        color: 'white',
                        fontSize: '1.25rem',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: 0,
                        lineHeight: 1,
                      }}
                    >
                      +
                    </button>
                  ) : null}
                </div>
              ))}
              <button
                type="submit"
                style={{
                  width: '100%',
                  padding: '0.85rem 1.5rem',
                  background: '#1c3dfe',
                  color: 'white',
                  border: 'none',
                  borderRadius: '9999px',
                  fontFamily: lexend,
                  fontOpticalSizing: 'auto',
                  fontSize: '1rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  boxShadow: '0 4px 14px rgba(28, 61, 254, 0.4)',
                  marginTop: '0.5rem',
                }}
              >
                Send Invites
              </button>
              <button
                type="button"
                onClick={handleNoThanks}
                style={{
                  width: '100%',
                  padding: '0.75rem 1.5rem',
                  background: 'transparent',
                  color: '#666',
                  border: '2px solid #e0e0e0',
                  borderRadius: '9999px',
                  fontFamily: lexend,
                  fontOpticalSizing: 'auto',
                  fontSize: '0.95rem',
                  fontWeight: 500,
                  cursor: 'pointer',
                }}
              >
                I don't have travel friends
              </button>
            </form>
              </>
            ) : (
              <div style={{ textAlign: 'center', padding: '1rem 0' }}>
                <img
                  src={check3dImg}
                  alt=""
                  style={{ width: '80px', height: 'auto', marginBottom: '1.25rem' }}
                />
                <h2 style={{
                  fontFamily: lexend,
                  fontOpticalSizing: 'auto',
                  fontWeight: 800,
                  fontSize: '1.35rem',
                  color: '#1c3dfe',
                  marginBottom: '0.75rem',
                  lineHeight: 1.3,
                }}>
                  Congratulations Adventurer, You're On the List!
                </h2>
                <p style={{
                  fontFamily: lexend,
                  fontOpticalSizing: 'auto',
                  fontSize: '1rem',
                  color: '#555',
                  lineHeight: 1.5,
                  margin: 0,
                }}>
                  We look forward to helping you find great areas to stay in.
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default HomePage;
